import { Users } from 'lucide-react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'
import { FilterOptionList } from '@/components/filters/filter-menu'
import type { AdminUser } from '@/hooks/use-admin-users'
import {
  formatPlatformGroupLabel,
  getAdminUserGroupFilterOptions,
} from '@/lib/platform-assignable-groups'

export type AdminUsersSort =
  | 'name_asc'
  | 'name_desc'
  | 'status_asc'
  | 'status_desc'
  | 'groups_asc'
  | 'groups_desc'
  | 'joined_asc'
  | 'joined_desc'
  | 'last_login_asc'
  | 'last_login_desc'

export type AdminUsersSortColumn =
  | 'user'
  | 'status'
  | 'groups'
  | 'joined'
  | 'last_login'

export type AdminUsersFilterState = {
  search: string
  group: string
}

const ADMIN_USERS_SORT_COLUMN_MAP: Record<
  AdminUsersSortColumn,
  { asc: AdminUsersSort; desc: AdminUsersSort }
> = {
  user: { asc: 'name_asc', desc: 'name_desc' },
  status: { asc: 'status_asc', desc: 'status_desc' },
  groups: { asc: 'groups_asc', desc: 'groups_desc' },
  joined: { asc: 'joined_asc', desc: 'joined_desc' },
  last_login: { asc: 'last_login_asc', desc: 'last_login_desc' },
}

const ADMIN_USERS_DEFAULT_COLUMN_SORT: Record<AdminUsersSortColumn, 'asc' | 'desc'> = {
  user: 'asc',
  status: 'asc',
  groups: 'asc',
  joined: 'desc',
  last_login: 'desc',
}

export const ADMIN_USERS_SORT_LABELS: Record<AdminUsersSort, string> = {
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
  status_asc: 'Status A–Z',
  status_desc: 'Status Z–A',
  groups_asc: 'Groups A–Z',
  groups_desc: 'Groups Z–A',
  joined_desc: 'Newest joined',
  joined_asc: 'Oldest joined',
  last_login_desc: 'Recent login',
  last_login_asc: 'Oldest login',
}

export const ADMIN_USERS_SORT_OPTIONS = (
  Object.keys(ADMIN_USERS_SORT_LABELS) as AdminUsersSort[]
).map((key) => ({
  value: key,
  label: ADMIN_USERS_SORT_LABELS[key],
})) as Array<{ value: string; label: string }>

export type AdminUsersFilterCtx = Record<string, never>

export function adminUserSortName(user: AdminUser): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.email
}

function adminUserStatusSortKey(user: AdminUser): string {
  return user.is_active ? '0' : '1'
}

function adminUserGroupsSortKey(user: AdminUser): string {
  const groups = [...(user.groups ?? [])].sort((a, b) => a.localeCompare(b))
  return groups.join(', ').toLowerCase() || '\uffff'
}

function compareStrings(a: string, b: string, asc: boolean): number {
  const result = a.localeCompare(b, undefined, { sensitivity: 'base' })
  return asc ? result : -result
}

function compareLastLogin(a: AdminUser, b: AdminUser, asc: boolean): number {
  const aLogin = a.last_login
  const bLogin = b.last_login
  if (!aLogin && !bLogin) return 0
  if (!aLogin) return 1
  if (!bLogin) return -1
  const diff = Date.parse(aLogin) - Date.parse(bLogin)
  return asc ? diff : -diff
}

export function compareAdminUsers(a: AdminUser, b: AdminUser, sort: AdminUsersSort): number {
  switch (sort) {
    case 'name_asc':
      return compareStrings(adminUserSortName(a), adminUserSortName(b), true)
    case 'name_desc':
      return compareStrings(adminUserSortName(a), adminUserSortName(b), false)
    case 'status_asc':
      return compareStrings(adminUserStatusSortKey(a), adminUserStatusSortKey(b), true)
    case 'status_desc':
      return compareStrings(adminUserStatusSortKey(a), adminUserStatusSortKey(b), false)
    case 'groups_asc':
      return compareStrings(adminUserGroupsSortKey(a), adminUserGroupsSortKey(b), true)
    case 'groups_desc':
      return compareStrings(adminUserGroupsSortKey(a), adminUserGroupsSortKey(b), false)
    case 'joined_asc':
      return Date.parse(a.date_joined) - Date.parse(b.date_joined)
    case 'joined_desc':
      return Date.parse(b.date_joined) - Date.parse(a.date_joined)
    case 'last_login_asc':
      return compareLastLogin(a, b, true)
    case 'last_login_desc':
      return compareLastLogin(a, b, false)
    default:
      return Date.parse(b.date_joined) - Date.parse(a.date_joined)
  }
}

export function isAdminUsersColumnSort(sort: AdminUsersSort, column: AdminUsersSortColumn): boolean {
  const keys = ADMIN_USERS_SORT_COLUMN_MAP[column]
  return sort === keys.asc || sort === keys.desc
}

export function adminUsersSortDirection(sort: AdminUsersSort): 'asc' | 'desc' {
  return sort.endsWith('_asc') ? 'asc' : 'desc'
}

export function adminUsersSortDirectionForColumn(
  sort: AdminUsersSort,
  column: AdminUsersSortColumn,
): 'asc' | 'desc' | null {
  const keys = ADMIN_USERS_SORT_COLUMN_MAP[column]
  if (sort === keys.asc) return 'asc'
  if (sort === keys.desc) return 'desc'
  return null
}

export function toggleAdminUsersColumnSort(
  current: AdminUsersSort,
  column: AdminUsersSortColumn,
): AdminUsersSort {
  const keys = ADMIN_USERS_SORT_COLUMN_MAP[column]
  if (isAdminUsersColumnSort(current, column)) {
    return current === keys.asc ? keys.desc : keys.asc
  }
  const defaultDirection = ADMIN_USERS_DEFAULT_COLUMN_SORT[column]
  return keys[defaultDirection]
}

export function getAdminUsersFilterDefinitions(): FilterDefinition<
  AdminUsersFilterState,
  AdminUsersFilterCtx
>[] {
  return [
    {
      key: 'group',
      label: 'Group',
      icon: Users,
      displayValue: (filters) => {
        if (!filters.group) return 'All'
        return (
          getAdminUserGroupFilterOptions().find((option) => option.value === filters.group)?.label ??
          formatPlatformGroupLabel(filters.group)
        )
      },
      isNonDefault: (filters) => Boolean(filters.group),
      reset: (filters) => ({ ...filters, group: '' }),
      renderPopover: ({ filters, onChange }) => (
        <FilterOptionList
          type="single"
          options={getAdminUserGroupFilterOptions()}
          value={filters.group || 'all'}
          onValueChange={(value) =>
            onChange({ ...filters, group: value === 'all' ? '' : value })
          }
        />
      ),
    },
  ]
}
